import React from 'react';
import PropTypes from 'prop-types';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import InfoIcon from '@material-ui/icons/Info';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import { Button } from '@material-ui/core';

import './AlertsCard.css';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { C } from '../pointsWatching/constants';
import {
  empMeta,
  metaFromColor,
  displayName,
} from '../pointsWatching/employeeColors';
import {
  weekdayOf,
  labelOf,
  timeOf,
} from '../pointsWatching/pointsWatchingHelpers';

const POINT_NO_CLEAR_SUBJECT = 'POINT_NO_CLEAR_SUBJECT';

class AlertsCard extends React.PureComponent {
  state = {
    snoozeDuration: 1,
    showSnooze: false,
  };

  renderIcon = () => {
    const { classification } = this.props.alertEntry;
    switch (classification) {
      case 'info':
        return <InfoIcon style={{ fontSize: 24, color: '#2196f3' }} />;
      case 'critical':
        return <InfoIcon style={{ fontSize: 24, color: '#f44336' }} />;
      case 'warning':
        return <InfoIcon style={{ fontSize: 24, color: '#ff9800' }} />;
      default:
        return <InfoIcon style={{ fontSize: 24 }} />;
    }
  };

  handleResolve = () => {
    const { alertId } = this.props.alertEntry;
    this.props.resolveAlert(alertId);
    this.setState({ showSnooze: false });
    // this.props.removeSnoozedAlert();
  };

  // "No clear subject" alerts deep-link into Points Watching for the affected
  // entry's date + employee. Only navigable when the backend stamped a date.
  isNavigable = () =>
    this.props.alertEntry?.type === POINT_NO_CLEAR_SUBJECT &&
    !!this.props.alertEntry?.identifier?.date &&
    typeof this.props.onAlertNavigate === 'function';

  handleNavigate = () => {
    if (!this.isNavigable()) return;
    this.props.onAlertNavigate(this.props.alertEntry.identifier);
  };

  // Who may dismiss a single notification.
  //
  // This used to test `user.companyIds.length > 0`, which is not a
  // permission at all — it is a proxy for "multi-company account", and
  // only NDS accounts carry that list. Company owners have the field
  // absent entirely, so the × never appeared for them even though the
  // drawer's "Clear Notifications" button (same action, all rows at
  // once) is offered to exactly the owner tier below. Deleting one alert
  // was harder than deleting all of them.
  //
  // Now gated on the same roles as that button, and hidden at a
  // view-only location because resolving an alert is a write.
  canDismiss = () => {
    if (this.props.viewOnly) return false;
    const roles = this.props.user?.authority || [];
    return (
      this.props.user?.isOwner ||
      roles.includes('ROLE_OWNER') ||
      roles.includes('ROLE_TECH_ADMIN') ||
      roles.includes('ROLE_MASTER_ADMIN')
    );
  };

  // Shared "No / Delete" confirmation row (opened by the × dismiss icon).
  renderSnoozeConfirm = () => {
    if (!this.state.showSnooze) return null;
    return (
      <CardContent>
        <div className="snooze-row">
          <Button
            variant="contained"
            onClick={() => this.setState({ showSnooze: false })}
            style={{
              width: '100px',
              marginRight: '16px',
              marginBottom: '16px',
            }}
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={this.handleResolve}
            style={{
              width: '100px',
              marginRight: '16px',
              marginBottom: '16px',
              color: 'white',
              backgroundColor: 'darkred',
            }}
          >
            Delete
          </Button>
        </div>
      </CardContent>
    );
  };

  // "No clear subject" alert — same layout/typography as the default alert
  // (alert-title / alert-title-text / alert-timestamp) for a consistent drawer.
  // Line 1 mirrors the message line; line 2 mirrors the "<b>who -</b> when"
  // line, with the giving employee shown in their identity colour. Deep-links.
  renderNoClearSubject() {
    const { alertEntry } = this.props;
    const { timestamp, identifier = {} } = alertEntry;
    const navigable = this.isNavigable();

    const nameColor = metaFromColor(identifier.employeeColor)
      ? identifier.employeeColor
      : empMeta(identifier.userId, identifier.userName).ink;

    const machineLabel = identifier.machine
      ? `Machine ${identifier.machine}`
      : '';
    const timeLabel = identifier.signInTime
      ? timeOf(identifier.signInTime)
      : '';
    const dayLabel = identifier.date
      ? `${weekdayOf(identifier.date)}, ${labelOf(identifier.date)}`
      : timestamp;

    // Title by detector verdict (carried in the identifier); legacy alerts
    // without one keep the generic wording.
    const issue =
      identifier.faceVerdict === 'NO_HUMAN'
        ? 'No human detected'
        : identifier.faceVerdict === 'UNCLEAR'
        ? 'Person detected, image unclear'
        : 'No clear subject';
    const titleLine = machineLabel ? `${issue}: ${machineLabel}` : issue;
    const whenLine = [dayLabel, timeLabel].filter(Boolean).join(' · ');

    return (
      <Card className="alerts-card">
        <div className="card-header alerts-card-header">
          <div className="alert-title">
            <div>
              <span
                style={{
                  display: 'flex',
                  flexShrink: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 36,
                  width: 36,
                  borderRadius: '50%',
                  backgroundColor: C.amberBg,
                }}
              >
                <BrokenImageIcon style={{ fontSize: 18, color: C.amberIcon }} />
              </span>
            </div>
            <div
              style={{
                width: '100%',
                cursor: navigable ? 'pointer' : 'default',
              }}
              onClick={navigable ? this.handleNavigate : undefined}
            >
              <p className="alert-title-text">{titleLine}</p>
              <p className="alert-timestamp">
                <b style={{ color: nameColor }}>
                  {displayName(identifier.userName) || 'Employee'} -
                </b>{' '}
                {whenLine}
              </p>
            </div>
            {this.canDismiss() && (
              <div style={{ marginLeft: '-16px' }}>
                <DeleteOutlineIcon
                  style={{
                    visibility: this.state.showSnooze ? 'hidden' : 'visible',
                  }}
                  className="snooze-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ showSnooze: true });
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {this.renderSnoozeConfirm()}
      </Card>
    );
  }

  render() {
    const { alertEntry } = this.props;

    if (alertEntry.type === POINT_NO_CLEAR_SUBJECT) {
      return this.renderNoClearSubject();
    }

    const { timestamp, alertMessage, identifier } = alertEntry;

    return (
      <Card className="alerts-card">
        <div className="card-header alerts-card-header">
          <div className="alert-title">
            <div>{this.renderIcon()}</div>
            <div style={{ width: '100%' }}>
              <p className="alert-title-text">{alertMessage}</p>
              <p className="alert-timestamp">
                <b>{identifier?.userName} -</b> {timestamp}
              </p>
            </div>
            {this.canDismiss() && (
              <div style={{ marginLeft: '-16px' }}>
                <DeleteOutlineIcon
                  style={{
                    visibility: this.state.showSnooze ? 'hidden' : 'visible',
                  }}
                  className="snooze-icon"
                  onClick={() => this.setState({ showSnooze: true })}
                />
              </div>
            )}
          </div>
        </div>

        {this.renderSnoozeConfirm()}
      </Card>
    );
  }
}

AlertsCard.propTypes = {
  alertEntry: PropTypes.object.isRequired,
  snoozeAlert: PropTypes.func,
  removeSnoozedAlert: PropTypes.func,
  onAlertNavigate: PropTypes.func,
};

export default AlertsCard;
